// IntegrationsPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a
// IntegrationsView (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui só há lógica/dados. Backup do legado: IntegrationsPage.legacy.jsx.bak.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import whatsappService from '../services/whatsappService';
import billingService from '../services/billingService';
import { useMeter } from '../hooks/useEntitlements';
import useSnackbar from '../hooks/useSnackbar';
import AppSnackbar from '../components/ui/AppSnackbar';
import PixQrCheckout from '../components/billing/PixQrCheckout';
import IntegrationsView from './integrations/IntegrationsView';

// ── helpers de formatação (a View recebe strings prontas — não formata) ──────────
const hourToTime = (h) => `${String(h ?? 0).padStart(2, '0')}:00`;
const timeToHour = (t) => parseInt(String(t || '0').split(':')[0], 10) || 0;
const fmtBRL = (cents) => `R$ ${((cents ?? 0) / 100).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—');
const DIRECTION_MAP = { outbound: 'out', inbound: 'in' };
const PURPOSE_LABEL = {
  created: 'Agendamento criado',
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta automática',
  inbound: 'Resposta do cliente',
  owner_created: 'Aviso ao dono — novo agendamento',
  owner_confirmed: 'Aviso ao dono — confirmação',
  owner_cancelled: 'Aviso ao dono — cancelamento',
};
const STATUS_LABEL = { sent: 'Enviada', delivered: 'Entregue', read: 'Lida', failed: 'Falhou', undelivered: 'Não entregue', received: 'Recebida' };
const SOURCE_LABEL = { pack_purchase: 'Compra', plan_grant: 'Cota do plano', admin_grant: 'Cortesia / promoção', welcome_grant: 'Bônus de boas-vindas' };
const INTENT_STATUS = {
  fulfilled: { label: 'Pago', tone: 'paid' },
  pending:   { label: 'Aguardando pagamento', tone: 'pending' },
  failed:    { label: 'Falhou', tone: 'failed' },
  reversed:  { label: 'Estornado', tone: 'failed' },
};
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
// "+5541984029395" / "+554184029395" → "(41) 98402-9395" / "(41) 8402-9395"; formato inesperado → cru.
const fmtPhoneBR = (e164) => {
  const d = String(e164 ?? '').replace(/\D/g, '');
  if (!d) return '';
  const local = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return e164 ?? '';
};
const fmtDayShort = (isoDate) => { const [, m, d] = String(isoDate).split('-'); return `${d}/${m}`; };

const DEFAULT_SETTINGS = {
  enabled: false, templateStyle: 2, messageNotes: '',
  notifyOnPublicCreate: true, notifyOnPrivateCreate: true,
  reminderEnabled: false, reminderHoursBefore: 24, reminderRespectBusinessHours: true,
  reminderBusinessStart: 8, reminderBusinessEnd: 18,
  ownerNotifyEnabled: false, ownerPhone: '',
  ownerNotifyOnCreate: true, ownerNotifyOnConfirm: true, ownerNotifyOnCancel: true,
};

const FALLBACK_SAMPLE = `Oi, Maria! 😊 Deu tudo certo, o seu horário está confirmado!

✨ Serviço: Corte de cabelo
💈 Profissional: Ana
📍 Local: Salão X — Rua Y, 123
🗓️ Quando: 25/06/2026 às 14:30

Precisa mudar alguma coisa? É só acessar app.agendario.com.br/a/123.

Até logo! 💚`;

const clampHour = (v, fb) => { const n = parseInt(v, 10); return Number.isNaN(n) ? fb : Math.min(24, Math.max(0, n)); };

function buildPayload(src) {
  return {
    ...src,
    messageNotes: src.messageNotes?.trim() ? src.messageNotes.trim() : null,
    reminderHoursBefore: Math.min(168, Math.max(1, parseInt(src.reminderHoursBefore, 10) || 24)),
    reminderBusinessStart: clampHour(src.reminderBusinessStart, 8),
    reminderBusinessEnd: clampHour(src.reminderBusinessEnd, 18),
  };
}

function buildPreview(model, notes) {
  const trimmed = notes?.trim();
  if (!model) return FALLBACK_SAMPLE;
  return trimmed
    ? (model.previewCreatedWithNotes || '').replace('{{ORIENTACOES}}', trimmed)
    : (model.previewCreated || '');
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { balance: waBalance, low: waLow, loading: waLoading } = useMeter('whatsapp_messages');

  // ── settings ─────────────────────────────────────────────────────────────────
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [savedForm, setSavedForm] = useState(DEFAULT_SETTINGS);
  const [dirty, setDirty] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [models, setModels] = useState([]);

  // ── mensagens (histórico) ────────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary] = useState(null);

  // ── extrato de créditos (@v3) ────────────────────────────────────────────────
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(true);
  const [statementError, setStatementError] = useState(null);

  const loadStatement = useCallback(async () => {
    setStatementLoading(true); setStatementError(null);
    try { setStatement(await whatsappService.getCreditStatement(30)); }
    catch { setStatementError('Não foi possível carregar o extrato. Tente novamente.'); }
    finally { setStatementLoading(false); }
  }, []);
  useEffect(() => { loadStatement(); }, [loadStatement]);

  // ── pacotes + compra PIX ─────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const [cpfFor, setCpfFor] = useState(null);
  const [cpf, setCpf] = useState('');
  const [cpfSubmitting, setCpfSubmitting] = useState(false);
  const [cpfError, setCpfError] = useState(null);

  // Load settings + models on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        const merged = { ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' };
        setForm(merged);
        setSavedForm(merged);
        try { setModels(await whatsappService.getModels()); } catch { /* fallback */ }
      } catch { /* mantém defaults */ } finally { setLoadingSettings(false); }
    })();
  }, []);

  // Load products (pacotes)
  useEffect(() => {
    billingService.listProducts('whatsapp_messages')
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => { /* seção de pacotes fica vazia */ });
  }, []);

  // Load messages on page/rowsPerPage change
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally { setLoadingMessages(false); }
  }, []);
  useEffect(() => { loadMessages(page, rowsPerPage); }, [page, rowsPerPage, loadMessages]);

  // ── handlers ─────────────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await whatsappService.updateSettings(buildPayload(form));
      setSavedForm(form);
      setDirty(false);
      showSuccess('Configurações de WhatsApp salvas.');
    } catch { showError('Não foi possível salvar as configurações. Tente novamente.'); }
    finally { setSavingSettings(false); }
  }, [form, showSuccess, showError]);

  const handleDiscard = useCallback(() => { setForm(savedForm); setDirty(false); }, [savedForm]);

  const handleToggleEnabled = useCallback(async (next) => {
    setForm((f) => ({ ...f, enabled: next }));
    setTogglingEnabled(true);
    try {
      await whatsappService.updateSettings(buildPayload({ ...savedForm, enabled: next }));
      setSavedForm((s) => ({ ...s, enabled: next }));
      if (next) {
        // 1ª ativação concede 50 créditos no backend; sem refetch a tela expande com o
        // snapshot velho (saldo 0) e o usuário só vê o bônus após F5 — inclusive comprando à toa.
        queryClient.invalidateQueries({ queryKey: ['entitlements'] });
        loadStatement();
      }
      showSuccess(next ? 'WhatsApp ativado.' : 'WhatsApp desativado.');
    } catch {
      setForm((f) => ({ ...f, enabled: savedForm.enabled }));
      showError('Não foi possível atualizar. Tente novamente.');
    } finally { setTogglingEnabled(false); }
  }, [savedForm, showSuccess, showError, queryClient, loadStatement]);

  // Compra PIX (reuso do fluxo de ProductCatalog: createOrder → 400 pede CPF → PixQrCheckout)
  const startPurchase = useCallback((product, document) => {
    const input = { kind: 'pack', productId: product.id, methods: ['Pix'], providerSlug: 'asaas' };
    if (document) input.document = document;
    return billingService.createOrder(input);
  }, []);

  const handleBuy = useCallback(async (product) => {
    try {
      setCheckoutOrder(await startPurchase(product));
    } catch (err) {
      if (err?.response?.status === 400) { setCpf(''); setCpfError(null); setCpfFor(product); }
      else { showError('Não foi possível iniciar o pagamento. Tente novamente.'); }
    }
  }, [startPurchase, showError]);

  const handleCpfSubmit = useCallback(async () => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) { setCpfError('Digite um CPF válido (11 dígitos).'); return; }
    setCpfSubmitting(true); setCpfError(null);
    try {
      const order = await startPurchase(cpfFor, digits);
      setCpfFor(null);
      setCheckoutOrder(order);
    } catch (err) {
      setCpfError(err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Verifique o CPF e tente novamente.');
    } finally { setCpfSubmitting(false); }
  }, [cpf, cpfFor, startPurchase]);

  // ── ViewModel ────────────────────────────────────────────────────────────────
  const viewState = loadingSettings ? 'loading' : (form.enabled ? 'enabled' : 'disabled');
  const level = (waBalance ?? 0) === 0 ? 'zero' : (waLow ? 'low' : 'ok');
  const previewText = useMemo(
    () => buildPreview(models.find((m) => m.id === form.templateStyle), form.messageNotes) || FALLBACK_SAMPLE,
    [models, form.templateStyle, form.messageNotes],
  );

  const vm = {
    viewState,
    errorMessage: null,
    whatsapp: { enabled: form.enabled },
    credits: {
      balance: waBalance ?? 0,
      balanceLabel: `${(waBalance ?? 0).toLocaleString('pt-BR')} créditos`,
      // @v3: snapshot de saldo ainda carregando — a View mostra skeleton em vez do falso "0 créditos".
      loading: waLoading,
      level,
      statement: {
        loading: statementLoading,
        errorMessage: statementError,
        isEmpty: !statementLoading && !statementError
          && !(statement?.entries?.length) && !(statement?.purchases?.length) && !(statement?.daily?.length),
        entries: (statement?.entries ?? []).map((e) => {
          const expired = e.expiresAt && new Date(e.expiresAt) <= new Date();
          const depleted = (e.remaining ?? 0) <= 0 && !expired;
          return {
            id: String(e.id),
            dateLabel: fmtDay(e.createdAt),
            originLabel: e.source === 'pack_purchase'
              ? `Compra — ${e.productName ?? 'Pacote'}`
              : (SOURCE_LABEL[e.source] ?? e.source),
            qtyLabel: `+${Number(e.qty ?? 0).toLocaleString('pt-BR')}`,
            remainingLabel: (e.remaining ?? 0) > 0 && !expired
              ? `${Number(e.remaining).toLocaleString('pt-BR')} restantes` : 'esgotado',
            validityLabel: e.expiresAt
              ? `${expired ? 'expirou' : 'expira'} ${fmtDay(e.expiresAt)}` : 'não expira',
            status: expired ? 'expired' : (depleted ? 'depleted' : 'active'),
          };
        }),
        purchases: (statement?.purchases ?? []).map((p) => {
          const st = INTENT_STATUS[p.status] ?? { label: p.status, tone: 'pending' };
          return {
            id: String(p.intentId),
            dateLabel: fmtDate(p.createdAt),
            packLabel: p.productName ?? 'Pacote',
            qtyLabel: p.creditQty ? `${Number(p.creditQty).toLocaleString('pt-BR')} mensagens` : '—',
            priceLabel: p.priceCents != null ? fmtBRL(p.priceCents) : '—',
            statusLabel: st.label,
            statusTone: st.tone,
          };
        }),
        usage: (() => {
          const days = (statement?.daily ?? []).map((d) => ({ dateLabel: fmtDayShort(d.date), consumed: d.consumed }));
          const totalConsumed = (statement?.daily ?? []).reduce((acc, d) => acc + (d.consumed ?? 0), 0);
          return {
            isEmpty: days.length === 0,
            days,
            maxConsumed: days.reduce((m, d) => Math.max(m, d.consumed), 0),
            totalLabel: days.length ? `${totalConsumed.toLocaleString('pt-BR')} mensagens nos últimos ${statement?.windowDays ?? 30} dias` : '',
          };
        })(),
      },
    },
    message: {
      models: models.length ? models : [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }],
      selectedModelId: form.templateStyle,
      freeText: form.messageNotes,
      previewBubbles: [{ from: 'business', text: previewText, time: '14:30', hasButtons: form.reminderEnabled }],
    },
    notifyOnCreate: { fromPublicSite: form.notifyOnPublicCreate, fromPanel: form.notifyOnPrivateCreate },
    reminder: {
      enabled: form.reminderEnabled,
      hoursBefore: Math.min(168, Math.max(1, parseInt(form.reminderHoursBefore, 10) || 24)),
      respectBusinessHours: form.reminderRespectBusinessHours,
      businessStart: hourToTime(form.reminderBusinessStart),
      businessEnd: hourToTime(form.reminderBusinessEnd),
    },
    ownerNotify: {
      enabled: form.ownerNotifyEnabled,
      phone: form.ownerPhone,
      phoneError: null,
      events: { newBooking: form.ownerNotifyOnCreate, clientConfirmed: form.ownerNotifyOnConfirm, clientCancelled: form.ownerNotifyOnCancel },
    },
    packages: products.map((p, i) => ({
      id: String(p.id),
      qty: Number(p.creditQty ?? 0),
      qtyLabel: `${Number(p.creditQty ?? 0).toLocaleString('pt-BR')} mensagens`,
      priceLabel: fmtBRL(p.priceCents),
      perMsgLabel: p.creditQty ? `${fmtBRL(p.priceCents / p.creditQty)} por mensagem` : '',
      validityLabel: p.validityDays ? `Validade ${p.validityDays} dias` : '',
      highlighted: products.length > 1 && i === Math.floor(products.length / 2),
    })),
    history: {
      isEmpty: !loadingMessages && total === 0 && !messagesError,
      summary: { sent: summary?.sent ?? 0, received: summary?.received ?? 0, failed: summary?.failedNoCredit ?? 0 },
      rows: messages.map((m, idx) => ({
        id: String(m.id ?? idx),
        dateLabel: fmtDate(m.createdAt),
        direction: DIRECTION_MAP[m.direction] ?? 'out',
        typeLabel: PURPOSE_LABEL[m.purpose] ?? m.purpose ?? '—',
        statusLabel: STATUS_LABEL[m.status] ?? m.status ?? '—',
        phoneMasked: m.phoneMasked,
        // @v3: telefone real (cliente do próprio dono), corpo da inbound e link p/ o cliente.
        phoneLabel: fmtPhoneBR(m.phone) || m.phoneMasked || '—',
        bodyPreview: m.bodyPreview ?? null,
        personId: m.personId ?? null,
        personName: m.personName ?? null,
        personHref: m.personId ? `/persons/${m.personId}` : null,
      })),
      page: page + 1,
      pageCount: Math.max(1, Math.ceil(total / rowsPerPage)),
      total,
      rowsPerPage,
      errorMessage: messagesError,
      loading: loadingMessages,
    },
    saving: savingSettings,
    dirty,
    togglingEnabled,
  };

  const actions = {
    onToggleWhatsapp: handleToggleEnabled,
    onActivate: () => handleToggleEnabled(true),
    onSelectModel: (id) => updateField('templateStyle', id),
    onChangeFreeText: (t) => updateField('messageNotes', t),
    onToggleNotifyOnCreate: (key, next) => updateField(key === 'fromPublicSite' ? 'notifyOnPublicCreate' : 'notifyOnPrivateCreate', next),
    onToggleReminder: (next) => updateField('reminderEnabled', next),
    onChangeReminderHours: (h) => updateField('reminderHoursBefore', h),
    onToggleRespectBusinessHours: (next) => updateField('reminderRespectBusinessHours', next),
    onChangeBusinessHours: (start, end) => { updateField('reminderBusinessStart', timeToHour(start)); updateField('reminderBusinessEnd', timeToHour(end)); },
    onToggleOwnerNotify: (next) => updateField('ownerNotifyEnabled', next),
    onChangeOwnerPhone: (p) => updateField('ownerPhone', p),
    onToggleOwnerEvent: (key, next) => updateField({ newBooking: 'ownerNotifyOnCreate', clientConfirmed: 'ownerNotifyOnConfirm', clientCancelled: 'ownerNotifyOnCancel' }[key], next),
    onBuyPackage: (id) => { const p = products.find((x) => String(x.id) === String(id)); if (p) handleBuy(p); },
    onChangeHistoryPage: (pg) => setPage(Math.max(0, pg - 1)),
    onChangeHistoryRowsPerPage: (rpp) => { setRowsPerPage(rpp); setPage(0); },
    onRefreshHistory: () => loadMessages(page, rowsPerPage),
    onDismissHistoryError: () => setMessagesError(null),
    onRetryStatement: loadStatement,
    onSave: handleSave,
    onDiscard: handleDiscard,
  };

  return (
    <>
      <IntegrationsView vm={vm} actions={actions} />

      {/* Modal de CPF (1ª compra sem CPF salvo) */}
      <Dialog open={!!cpfFor} onClose={() => { if (!cpfSubmitting) setCpfFor(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Digite seu CPF para gerar o QR Code</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)}
            error={!!cpfError} helperText={cpfError || 'Só na 1ª compra. Fica salvo para as próximas.'} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCpfFor(null)} disabled={cpfSubmitting}>Cancelar</Button>
          <Button variant="contained" onClick={handleCpfSubmit} disabled={cpfSubmitting}>Gerar QR Code</Button>
        </DialogActions>
      </Dialog>

      {/* Ao fechar o checkout, recarrega o extrato (o pagamento pode ter concedido lote novo). */}
      {checkoutOrder && <PixQrCheckout order={checkoutOrder} onClose={() => { setCheckoutOrder(null); loadStatement(); }} />}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
